/**
 * CommandPalette - Quick actions via ⌘/Ctrl+K
 *
 * Features:
 * - Global keyboard shortcut (⌘/Ctrl+K)
 * - Fuzzy search
 * - Recent actions (localStorage)
 * - Keyboard navigation (arrow keys, enter, escape)
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shadcn/command';
import {
  Home,
  List,
  Calculator,
  Palette,
  Settings,
  Search,
  FileText,
  Plus,
  Clock,
} from 'lucide-react';

interface CommandAction {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  keywords?: string[];
  onSelect: () => void;
}

const RECENT_ACTIONS_KEY = 'trueprice:recent-commands';
const MAX_RECENT = 5;

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [recentActions, setRecentActions] = useState<string[]>([]);

  // Load recent actions from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_ACTIONS_KEY);
      if (stored) {
        setRecentActions(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('[CommandPalette] Failed to load recent actions:', error);
    }
  }, []);

  // Save recent action
  const saveRecentAction = useCallback((actionId: string) => {
    setRecentActions((prev) => {
      // Add to front, remove duplicates, limit to MAX_RECENT
      const updated = [actionId, ...prev.filter((id) => id !== actionId)].slice(0, MAX_RECENT);
      try {
        localStorage.setItem(RECENT_ACTIONS_KEY, JSON.stringify(updated));
      } catch (error) {
        console.warn('[CommandPalette] Failed to save recent actions:', error);
      }
      return updated;
    });
  }, []);

  // All available actions
  const actions: CommandAction[] = [
    {
      id: 'new-audit',
      label: 'New Audit',
      description: 'Price a new piece',
      icon: Plus,
      keywords: ['create', 'add', 'new', 'audit', 'price'],
      onSelect: () => {
        navigate('/app/calculate');
        saveRecentAction('new-audit');
      },
    },
    {
      id: 'dashboard',
      label: 'Go to Dashboard',
      description: 'View your studio overview',
      icon: Home,
      keywords: ['home', 'dashboard', 'overview'],
      onSelect: () => {
        navigate('/app/portfolio');
        saveRecentAction('dashboard');
      },
    },
    {
      id: 'portfolio',
      label: 'Go to Portfolio',
      description: 'View saved prices',
      icon: List,
      keywords: ['portfolio', 'prices', 'saved', 'verdicts'],
      onSelect: () => {
        navigate('/app/portfolio');
        saveRecentAction('portfolio');
      },
    },
    {
      id: 'studio',
      label: 'Go to Studio',
      description: 'View analytics',
      icon: Palette,
      keywords: ['studio', 'analytics', 'insights'],
      onSelect: () => {
        navigate('/app/studio');
        saveRecentAction('studio');
      },
    },
    {
      id: 'settings',
      label: 'Open Settings',
      description: 'Manage your account',
      icon: Settings,
      keywords: ['settings', 'preferences', 'profile', 'account'],
      onSelect: () => {
        window.dispatchEvent(new CustomEvent('open-settings'));
        saveRecentAction('settings');
      },
    },
    {
      id: 'search',
      label: 'Search Prices',
      description: 'Find saved prices',
      icon: Search,
      keywords: ['search', 'find', 'verdicts', 'audits'],
      onSelect: () => {
        navigate('/app/portfolio');
        saveRecentAction('search');
      },
    },
  ];

  // Get recent actions data
  const recentActionsData = recentActions
    .map((id) => actions.find((a) => a.id === id))
    .filter(Boolean) as CommandAction[];

  // Keyboard shortcut handler
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Recent Actions */}
        {recentActionsData.length > 0 && (
          <CommandGroup heading="Recent">
            {recentActionsData.map((action) => {
              const Icon = action.icon;
              return (
                <CommandItem
                  key={action.id}
                  onSelect={() => {
                    action.onSelect();
                    setOpen(false);
                  }}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  <span>{action.label}</span>
                  {action.description && (
                    <span className="ml-auto text-xs text-charcoal-400">{action.description}</span>
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* All Actions */}
        <CommandGroup heading="Actions">
          {actions.map((action) => {
            const Icon = action.icon;
            return (
              <CommandItem
                key={action.id}
                keywords={action.keywords}
                onSelect={() => {
                  action.onSelect();
                  setOpen(false);
                }}
              >
                <Icon className="w-4 h-4 mr-2" />
                <span>{action.label}</span>
                {action.description && (
                  <span className="ml-auto text-xs text-charcoal-400">{action.description}</span>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>

        {/* Help Text */}
        <div className="p-2 text-xs text-center text-charcoal-400 border-t border-cream-300">
          Press{' '}
          <kbd className="px-1.5 py-0.5 bg-cream-200 rounded text-[10px] font-mono">Esc</kbd> to
          close
        </div>
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
